// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import showdown from 'showdown';

// Deep import is deliberate: the `@coursera-web/cml` public barrel eagerly loads
// CMLViewer → AssetManager → a module-load side effect (FourHundred) that throws
// in this bundle's unit tests (see MessageBodyEditor for the same hazard with the
// editor chunk). `HTMLToCMLConverter` is a plain class with no store/React
// side effects, so importing it directly is safe and avoids evaluating the barrel.
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import HTMLToCMLConverter from 'bundles/cml/legacy/models/HTMLToCMLConverter';
import {
  PERSONALIZATION_TAGS,
  applyPersonalizationPlaceholders,
} from 'bundles/enterprise-admin-messaging-v2/utils/personalizationTags';

/**
 * CML serialization helpers for the messaging-v2 flows.
 *
 * - `compileMessageCml`: markdown body → CML `co-content` (send direction).
 * - `cmlToMessageMarkdown`: CML `co-content` → editable markdown (edit/resend).
 *
 * Personalization tokens (`<ptag>…</ptag>`) live in `personalizationTags`; this
 * module only handles the surrounding CML structure.
 */

/** CML email content envelope the triggered-messages API expects. */
export type CmlMessageContent = {
  typeName: 'cml';
  definition: { dtdId: 'email/1'; value: string };
};

// ─── markdown → CML (send) ──────────────────────────────────────────────────

const escapeXml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// The body is authored as markdown (the AI generates markdown; the editor reads
// and writes markdown — see MessageBodyEditor). The send payload, however, must
// be CML `co-content` just like the legacy CMLEditor flow (links → `<a>`, bold
// → `<strong>`, …). We get there by reusing the two converters the codebase
// already trusts: showdown (markdown → HTML, same engine as mockCompileEmailHtml)
// and cml's `HTMLToCMLConverter` (HTML → CML). `noHeaderId` keeps headings
// attribute-free.
const markdownConverter = new showdown.Converter({
  simplifiedAutoLink: false,
  tables: false,
  noHeaderId: true,
});

// HTMLToCMLConverter only maps headings h1–h3 (anything else is dropped+logged).
// The editor offers H4, so downgrade h4–h6 to h3 rather than silently lose the
// line; one level of heading fidelity is an acceptable trade for not dropping content.
const clampHeadings = (html: string): string => html.replace(/<(\/?)h[4-6]\b[^>]*>/gi, '<$1h3>');

const textBlock = (paragraph: string): string => `<text>${escapeXml(paragraph)}</text>`;

/**
 * Converts ONE paragraph of markdown into its CML block(s) (`<text>`, `<heading>`,
 * …). We compile paragraph-by-paragraph rather than the whole body at once for two
 * reasons: (1) the editor separates paragraphs with a single `\n` (see
 * MessageBodyEditor), which showdown would otherwise fold into one run-on `<p>`;
 * (2) HTMLToCMLConverter silently DROPS blocks it can't model (a paragraph holding
 * inline `code`, an image, …) instead of throwing, so converting one paragraph at a
 * time lets us detect an empty result and preserve the raw text rather than lose it.
 */
const compileParagraphToCml = (paragraph: string): string => {
  try {
    const html = clampHeadings(markdownConverter.makeHtml(escapeXml(paragraph)));
    // shouldWrap=false → inner CML only; we add the single `<co-content>` wrapper once.
    const cml = new HTMLToCMLConverter().toCML(html, false);
    if (cml.trim()) {
      return cml;
    }
  } catch {
    // Fall through to plain-text preservation below.
  }
  return textBlock(paragraph);
};

/**
 * Compiles the draft's markdown body into CML `co-content` — the only content
 * type the existing triggered-messages API accepts (mirroring the legacy
 * messaging flow, which sends CML directly). Inline formatting becomes real CML
 * elements (e.g. a markdown link `[text](url)` → `<a href="url">text</a>`), then
 * personalization tokens (`[Learner name]`) are swapped for their `<ptag>`
 * placeholders. The CTA is NOT included here — the API renders it from the
 * `callToAction` preset.
 *
 * Angle brackets are pre-escaped so literal `<…>` in the body can't be parsed as
 * (and dropped as) unsupported HTML; markdown control characters are unaffected.
 */
export const compileMessageCml = (message: string): CmlMessageContent => {
  const body = message
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(compileParagraphToCml)
    .join('');
  const value = applyPersonalizationPlaceholders(`<co-content>${body}</co-content>`);
  return { typeName: 'cml', definition: { dtdId: 'email/1', value } };
};

// ─── CML → markdown (edit / resend) ─────────────────────────────────────────

/**
 * Readable token for a `<ptag>` id. Supported tags (Learner name, Enrollment)
 * use their canonical editor token; any other tag the backend stored (e.g.
 * `PROGRAM_NAME`) is shown as a sentence-cased read-only token like
 * `[Program name]` so nothing is silently dropped.
 */
const tokenForTag = (id: string): string => {
  const supported = PERSONALIZATION_TAGS.find((tag) => tag.id === id);
  if (supported) {
    return supported.display;
  }
  const sentenceCased = id
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
  return `[${sentenceCased}]`;
};

/** Serialize one CML node to text, swapping `<ptag>`s for readable tokens. */
const renderNode = (node: ChildNode): string => {
  if (node.nodeType === 3 /* TEXT_NODE */) {
    return node.textContent ?? '';
  }
  if (node.nodeName.toLowerCase() === 'ptag') {
    return tokenForTag((node.textContent ?? '').trim());
  }
  // Any other element (nested formatting): flatten to its text, still catching
  // nested ptags. Inline marks (bold/italic) aren't emitted as markdown yet —
  // the authored bodies are plain text + tags; revisit if rich CML shows up.
  return Array.from(node.childNodes).map(renderNode).join('');
};

/**
 * Converts a sent message's CML body (`cmlContent.value`) into the markdown the
 * Edit-and-resend editor expects. Each top-level `<text>` block becomes a
 * paragraph (joined by blank lines), and personalization `<ptag>`s become the
 * editor's readable tokens (e.g. `[Learner name]`) — the inverse of the
 * send-time `applyPersonalizationPlaceholders`.
 *
 * Returns an empty string when there's no body or it can't be parsed.
 */
export const cmlToMessageMarkdown = (cmlValue: string): string => {
  // Parsing relies on the DOM; the editor (and therefore this conversion) only
  // runs client-side, so bail out cleanly on the server.
  if (!cmlValue || typeof document === 'undefined') {
    return '';
  }

  const container = document.createElement('div');
  container.innerHTML = cmlValue;
  const root = container.querySelector('co-content');
  if (!root) {
    return '';
  }

  return Array.from(root.children)
    .filter((child) => child.tagName.toLowerCase() === 'text')
    .map((block) => Array.from(block.childNodes).map(renderNode).join(''))
    .join('\n\n');
};
